import { Typography, Rating, Button, Box, Dialog, Stack, TextField } from "@mui/material"
import { useState, useEffect, useRef } from "react";
import { DoNotDisturb, Star, StarBorder } from '@mui/icons-material';
import { AdvisorPreview } from "../../../advisor";
import { IMeeting } from "../../../../types";
import { RATING_LABELS } from "../../../../constants/ratings";
import { useDealContext } from "../../../deal/context";

export const RatingDialog = ({ meeting, setIsRatingDialogOpen, onRate, onClose, suggestedRating }: { meeting: IMeeting, setIsRatingDialogOpen: (open: boolean) => void, onRate: (meetingUuid: string, rating: number | null, markAsOpportunity: boolean, comment: string | null) => void, onClose: () => void, suggestedRating: number | null }) => {
    const [rating, setRating] = useState<number | null>(meeting.ratings?.[0]?.rating ?? null);
    const { dealContext } = useDealContext();
    const [isOpportunity, setIsOpportunity] = useState(false);
    const [hoveredRating, setHoveredRating] = useState<number | null>(null);
    const [comment, setComment] = useState<string | null>(meeting.ratings?.[0]?.comment || "");
    const hasHandledInitialRating = useRef(false);
    const [step, setStep] = useState(0);

    const project = dealContext.project;

    useEffect(() => {
        if (suggestedRating !== null && !rating && !hasHandledInitialRating.current) {
            hasHandledInitialRating.current = true;
            handleRatingChange(null, suggestedRating);
        }
    }, [suggestedRating]);

    const handleRatingChange = (_event: any, newValue: number | null) => {
        setRating(newValue);
        if (newValue !== null) {
            onRate(meeting.uuid, newValue, isOpportunity, comment);
        }
    };

    const handleSubmitFeedback = () => {
        onRate(meeting.uuid, rating, isOpportunity, comment);
        handleClose();
    }

    const handleClose = () => {
        if (rating !== null && rating >= 3 && step === 0) {
            setStep(1);
        } else {
            setIsRatingDialogOpen(false);
            onClose();
        }
    }

    if (!meeting.advisor.user) {
        return null;
    }

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '550px',
                    maxWidth: '550px'
                }
            }}
        >
            <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {step === 0 &&
                        <Box>
                            <h5>Please rate your meeting with {meeting.advisor.user.profile.name.split(' ')[0]} at {meeting.advisor.user.profile?.company?.name}</h5>
                            <p>We hope you had a great <strong>{meeting.scheduler?.type && project.effective_scheduler_properties?.[meeting.scheduler.type]?.name}</strong> meeting with {meeting.advisor.user.profile.name.split(' ')[0]} at {meeting.advisor.user.profile?.company?.name}. Before we schedule any additional meetings, please rate this meeting, which helps us steer more of the right executives your way.</p>
                            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', backgroundColor: "#ffffff", borderRadius: "20px", padding: "20px", border: "1px solid #c9c9c9" }}>
                                <AdvisorPreview advisor={meeting.advisor} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: "40px", width: '100%' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DoNotDisturb
                                                onClick={() => {
                                                    onRate(meeting.uuid, 0, isOpportunity, comment);
                                                    setIsRatingDialogOpen(false);
                                                }}
                                                onMouseOver={(e) => {
                                                    setHoveredRating(0);
                                                }}
                                                onMouseLeave={(e) => {
                                                    setHoveredRating(-1);
                                                }}
                                                sx={{
                                                    cursor: 'pointer',
                                                    width: '50px',
                                                    height: '50px',
                                                    paddingBottom: '3px',
                                                    color: rating === 0 ? 'error.main' : 'action.disabled',
                                                    '&:hover': { color: 'error.main' }
                                                }}
                                            />
                                            <Rating
                                                value={rating}
                                                max={4}
                                                size="large"
                                                icon={<Star sx={{ width: '50px', height: '50px' }} />}
                                                emptyIcon={<StarBorder sx={{ width: '50px', height: '50px' }} />}
                                                onChange={handleRatingChange}
                                                onChangeActive={(_event, newHover) => {
                                                    setHoveredRating(newHover);
                                                }}
                                            />
                                        </Box>
                                        <Typography>
                                            {hoveredRating !== null && hoveredRating !== -1
                                                ? RATING_LABELS[hoveredRating]
                                                : rating !== null
                                                    ? RATING_LABELS[rating]
                                                    : "Please rate the meeting"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            {rating !== null &&
                                <Stack mt={2} gap={2} direction="column" alignItems="flex-end">
                                    <Box sx={{ width: '100%' }}>
                                        { rating < 4 ? (
                                            <Typography variant="body1" fontWeight="bold" mb={1}>What kept this meeting from being great?</Typography>
                                        ) : (
                                            <Typography variant="body1" fontWeight="bold" mb={1}>What made this meeting great?</Typography>
                                        )}
                                        <TextField
                                            label="Write 1 or 2 sentences that will help us improve these moving forward."
                                            multiline
                                            rows={2}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                        />
                                    </Box>
                                    <Button variant="contained" onClick={handleSubmitFeedback}>Submit Feedback</Button>
                                </Stack>
                            }
                        </Box>
                    }
                    {step === 1 && rating !== null && rating >= 3 &&
                        <Box>
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center", paddingBottom: "20px", justifyContent: "space-between" }}>
                                <Typography sx={{ fontSize: "1.2rem", fontWeight: "700" }}>Did this turn into an opportunity?</Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>

                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            backgroundColor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Button
                                            sx={{
                                                borderRadius: 0,
                                                borderRight: '1px solid',
                                                borderColor: 'divider',
                                                backgroundColor: !isOpportunity ? 'grey.700' : 'transparent',
                                                color: !isOpportunity ? 'primary.contrastText' : 'text.primary',
                                                '&:hover': {
                                                    backgroundColor: !isOpportunity ? 'grey.800' : 'action.hover',
                                                }
                                            }}
                                            onClick={() => {
                                                setIsOpportunity(false);
                                                if (rating !== null) {
                                                    onRate(meeting.uuid, rating, false, comment);
                                                }
                                            }}
                                        >
                                            Not Yet
                                        </Button>
                                        <Button
                                            sx={{
                                                borderRadius: 0,
                                                backgroundColor: isOpportunity ? 'primary.dark' : 'transparent',
                                                color: isOpportunity ? 'primary.contrastText' : 'text.primary',
                                                '&:hover': {
                                                    backgroundColor: isOpportunity ? 'primary.900' : 'action.hover',
                                                }
                                            }}
                                            onClick={() => {
                                                setIsOpportunity(true);
                                                if (rating !== null) {
                                                    onRate(meeting.uuid, rating, true, comment);
                                                }
                                            }}
                                        >
                                            Yes
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                            <p>Understanding if there's an opportunity helps us nurture them more effectively.</p>
                        </Box>
                    }
                </Box>
            </Box>
        </Dialog>
    );
};
